import type { AjustesBuzon, CanalConectado, CambioAjustesBuzon, ConsultaConversaciones, Conversacion, ListaConversaciones, Mensaje, MotivoEscalado, NivelSaldo, PlantillaMeta } from '../../api/ops/buzon';
import type { TipoFranja } from '../../logica/franjas';
import type { NombreIcono } from '../../componentes/iconos';

/**
 * Reglas del Buzón (11.3) sin estilos: filtros, estado de la conversación, pie (tomar, escribir o plantilla), fechas y
 * los textos del diseño (`diseno/canales-asistente/11.3-11.9-buzon-y-voz.html`). Quién puede qué sale de los permisos
 * efectivos (`canales.buzon.atender` con «todo» o con «lo suyo», G87); la API protege cada petición.
 */

export const CANALES: Readonly<Record<string, readonly [string, NombreIcono]>> = {
    whatsapp: ['WhatsApp', 'message-circle'],
    instagram: ['Instagram', 'camera'],
    telegram: ['Telegram', 'send'],
    email: ['Email', 'mail'],
    web_chat: ['Chat de la web', 'globe'],
    voice: ['Teléfono', 'phone'],
};

/** Nombre e icono de un tipo de canal; uno nuevo (el contrato admite que crezca) con su código y un icono genérico. */
export function canal(tipo: string): readonly [string, NombreIcono] {
    return CANALES[tipo] ?? [tipo, 'message-circle'];
}

/** Icono del botón de ajustes (la rueda, «settings» de Lucide; no es el grupo del menú). */
export const ICONO_AJUSTES: NombreIcono = `settings`;

/** Canales con la ventana de 24 h de Meta. */
export const CON_VENTANA: readonly string[] = ['whatsapp', 'instagram'];
/** Canales en los que la primera respuesta lleva T40 (G191, G526). */
export const CON_AVISO: readonly string[] = ['whatsapp', 'instagram', 'telegram'];
/** Canales sin acuse de lectura: llegan a «Entregado», nunca a «Leído» (G527). */
const SIN_LEIDO: readonly string[] = ['instagram', 'telegram', 'web_chat'];

export const MOTIVOS: Readonly<Record<MotivoEscalado, string>> = {
    customer_request: 'lo pidió el cliente',
    frustration: 'el cliente parece molesto',
    cannot_resolve: 'el asistente no podía resolverlo',
    quota_exhausted: 'no quedan conversaciones del asistente este mes',
    assistant_off: 'el asistente está apagado',
    excluded_contact: 'es un contacto sin asistente',
    no_entitlement: 'no tienes el bloque Asistente IA',
    season_pause: 'el negocio está en pausa de temporada',
    email_reply: 'es una respuesta a un email',
    read_only: 'el negocio está en solo lectura',
    channel_not_enabled: 'el asistente no atiende este canal',
    business_app_reply: 'contestaste desde la app WhatsApp Business',
    whatsapp_no_balance: 'te has quedado sin mensajes de WhatsApp',
};

// ---- Quién soy en el buzón ----

export interface Quien {
    /** Ve y atiende todas (Propietario, Administrador, Encargado). */
    todas: boolean;
    /** Solo las suyas y las que esperan a una persona (Recepción / Puerta, G87). */
    suyas: boolean;
    /** Mi `member_id` en este negocio. */
    yo: string | null;
    /** Ajustes del buzón (`canales.buzon.configurar`). */
    configura: boolean;
    /** El negocio tiene el bloque Asistente IA (`listEntitlements`); sin él, todo llega para una persona (G453). */
    asistente: boolean;
}

// ---- Filtros (RV Filtros) y «Mostrar» ----

export type ClaveFiltro = 'todas' | 'sin-leer' | 'persona' | 'asistente' | 'cerradas';

export const FILTROS: readonly (readonly [ClaveFiltro, string])[] = [
    ['todas', 'Todas'],
    ['sin-leer', 'Sin leer'],
    ['persona', 'Con una persona'],
    ['asistente', 'Con el asistente'],
    ['cerradas', 'Cerradas'],
];

/** Recepción no ve las del asistente (G87) y sin el bloque Asistente IA no hay (G453): sin ese filtro. */
export function filtrosDe(q: Quien): readonly (readonly [ClaveFiltro, string])[] {
    return FILTROS.filter(([c]) => (q.todas && q.asistente) || c !== 'asistente');
}

export type Asignadas = 'any' | 'me' | 'none';

/** Con los contadores del servidor (`ConversationList.counts`, G728), «Asignadas a mí · 3» y «Sin asignar · 2». */
export function opcionesMostrar(q: Quien, cuentas: Contadores | null = null): readonly { valor: Asignadas; texto: string }[] {
    const con = (texto: string, n: number | undefined) => (n === undefined || n === 0 ? texto : `${texto} · ${n.toLocaleString('es-ES')}`);
    return [
        { valor: 'any', texto: 'Todas' },
        { valor: 'me', texto: con('Asignadas a mí', cuentas?.mine) },
        { valor: 'none', texto: con(q.todas ? 'Sin asignar' : 'Esperan a una persona', cuentas?.unassigned) },
    ];
}

export type Contadores = ListaConversaciones['counts'];

// ---- Búsqueda (G728) ----

/** `q` de `listConversations`: de 3 a 100 letras; con menos no se busca (ni se llama con ella). */
export const MIN_BUSQUEDA = 3;
export const MAX_BUSQUEDA = 100;
export function busquedaValida(texto: string): string | null {
    const t = texto.trim();
    return t.length >= MIN_BUSQUEDA ? t.slice(0, MAX_BUSQUEDA) : null;
}

// ---- Saldo de WhatsApp (G726) ----

/**
 * La franja del saldo de WhatsApp (`whatsapp_balance_level` de la lista y de la conversación): solo el nivel, sin
 * cifras. Con `plan.ver`, «Añadir mensajes» lleva a Plan; sin él, «Avisa al Propietario». Nada con `ok` o sin WhatsApp.
 */
export function franjaDeSaldo(nivel: NivelSaldo | null | undefined, verPlan: boolean): { tipo: TipoFranja; titulo: string; accion: string | null } | null {
    if (nivel !== 'low' && nivel !== 'empty') {
        return null;
    }
    const base = nivel === 'low' ? 'Quedan pocos mensajes de WhatsApp' : 'Sin mensajes de WhatsApp';
    return { tipo: nivel === 'low' ? 'cupo' : 'impago', titulo: verPlan ? base : `${base}. Avisa al Propietario`, accion: verPlan ? 'Añadir mensajes' : null };
}
/** Adónde lleva «Añadir mensajes» (Plan › Consumo, G120). */
export const RUTA_MENSAJES_PLAN = '/plan?apartado=consumo';

// ---- Adjuntos (G727) ----

/** Lo que admite cada canal (lo que deja el proveedor), para el selector de archivos; sin entrada, sin adjuntos. */
export const ADJUNTOS_POR_CANAL: Readonly<Record<string, readonly string[]>> = {
    whatsapp: ['image/jpeg', 'image/png', 'application/pdf', 'audio/aac', 'audio/mpeg', 'audio/ogg'],
    instagram: ['image/jpeg', 'image/png'],
    telegram: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'],
    email: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'],
    web_chat: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
};
export const MAX_ADJUNTO = 10 * 1024 * 1024;

/** Por qué no vale un archivo (antes de subirlo; la API lo vuelve a comprobar y pasa el antivirus). null si vale. */
export function errorDeAdjunto(archivo: { size: number; type: string }, tipoCanal: string): string | null {
    const tipos = ADJUNTOS_POR_CANAL[tipoCanal] ?? [];
    if (!tipos.includes(archivo.type)) {
        return `${canal(tipoCanal)[0]} no admite este tipo de archivo.`;
    }
    if (archivo.size > MAX_ADJUNTO) {
        return 'El archivo ocupa más de 10 MB.';
    }
    return null;
}

/** La consulta de `listConversations` de cada filtro. «Todas» son las abiertas: las cerradas se quitan al pintar. */
export function consultaDe(filtro: ClaveFiltro, canalId: string | null, asignadas: Asignadas, busca: string | null = null): ConsultaConversaciones {
    const base: ConsultaConversaciones = {};
    if (busca !== null) {
        base.q = busca;
    }
    if (canalId !== null) {
        base.channel_id = canalId;
    }
    if (asignadas !== 'any') {
        base.assigned = asignadas;
    }
    switch (filtro) {
        case 'sin-leer':
            return { ...base, unread: true };
        case 'persona':
            return { ...base, state: 'human' };
        case 'asistente':
            return { ...base, state: 'assistant' };
        case 'cerradas':
            return { ...base, state: 'closed' };
        default:
            return base;
    }
}

/** ¿Cabe en el filtro? (para quitar las cerradas de «Todas» y para no perder de vista la que se acaba de tocar). */
export function enFiltro(c: Pick<Conversacion, 'state' | 'unread_count'>, filtro: ClaveFiltro): boolean {
    switch (filtro) {
        case 'todas':
            return c.state !== 'closed';
        case 'sin-leer':
            return c.unread_count > 0;
        case 'persona':
            return c.state === 'human';
        case 'asistente':
            return c.state === 'assistant';
        default:
            return c.state === 'closed';
    }
}

export function vacioDe(filtro: ClaveFiltro, conFiltros: boolean, q: Quien): { titulo: string; texto: string } {
    if (conFiltros) {
        return { titulo: 'Nada con estos filtros.', texto: 'Prueba con otro canal o quita la búsqueda.' };
    }
    switch (filtro) {
        case 'sin-leer':
            return { titulo: 'Lo tienes todo leído.', texto: 'Los mensajes nuevos aparecerán aquí hasta que abras su conversación.' };
        case 'persona':
            return {
                titulo: 'Nadie espera a una persona.',
                texto: q.suyas ? 'Cuando una conversación espere a alguien del equipo o te la asignen, aparecerá aquí.' : 'Cuando el asistente pase una conversación al equipo, aparecerá aquí y te avisará la campana.',
            };
        case 'asistente':
            return { titulo: 'El asistente no atiende ninguna ahora.', texto: 'Las conversaciones que lleva el asistente aparecen aquí mientras duran.' };
        case 'cerradas':
            return { titulo: 'Todavía no hay conversaciones cerradas.', texto: 'Se cierran solas tras 24 h sin mensajes o cuando las cierras tú.' };
        default:
            return { titulo: 'No hay conversaciones abiertas.', texto: 'Cuando un cliente escriba por tus canales, la conversación aparecerá aquí.' };
    }
}

/** Opciones del filtro de canal: los canales del negocio (con su nombre si hay dos del mismo tipo). */
export function opcionesCanal(canales: readonly CanalConectado[]): { valor: string; texto: string }[] {
    const vivos = canales.filter((c) => c.status !== 'disconnected' && c.channel_type !== 'voice');
    return [
        { valor: '', texto: 'Todos los canales' },
        ...vivos.map((c) => {
            const [nombre] = canal(c.channel_type);
            const repetido = vivos.filter((x) => x.channel_type === c.channel_type).length > 1;
            return { valor: c.id, texto: repetido ? `${nombre} · ${c.display_name ?? c.display_identifier}` : nombre };
        }),
    ];
}

// ---- Nombres ----

export function nombreDe(c: Pick<Conversacion, 'contact'>): string {
    const n = c.contact.display_name?.trim();
    return n !== undefined && n !== '' ? n : c.contact.display_identifier;
}

export const primerNombre = (n: string): string => n.trim().split(/\s+/)[0] ?? n;

export function iniciales(n: string): string {
    const partes = n.replace(/[^\p{L}\p{N}\s]/gu, ' ').trim().split(/\s+/).filter((p) => p !== '');
    const [a = '', b = ''] = partes;
    return ((a[0] ?? '') + (b[0] ?? a[1] ?? '')).toUpperCase() || '·';
}

// ---- Estado de la conversación (cabecera en una línea) ----

/**
 * Prefijo del extracto en la lista del buzón (11.3, G1206, G1215), según quién escribió el último mensaje
 * (`last_message_origin`): «Tú: » solo si lo escribió quien mira; otra persona del equipo, «Nombre: » con su nombre de pila
 * (`last_message_member_name`, pregunta 780; G1226: «Tú» se decide por `last_message_member_id` frente al id de quien mira); «Asistente: », «Desde la app: » (app de WhatsApp Business), «Envío: » (Entregas) y
 * «Automático: » (mensaje automático o del sistema). El cliente, sin prefijo. Nulo (sin mensajes, fuera de retención o
 * anonimizado), sin prefijo.
 *
 * G1226: «quien mira» se reconoce comparando `last_message_member_id` con su id (`miId`). Solo si el id es nulo se
 * compara el nombre con el suyo en el equipo (`miNombre`), como antes. Sin nombre propio conocido se pone el nombre, nunca un «Tú» dudoso; sin nombre de autor (el contrato no lo
 * prevé con `staff`), sin prefijo.
 */
/**
 * El nombre del autor en el prefijo de la lista (pregunta 780, opción a): el nombre de pila, como el dibujo 11.3 («Ana: »).
 * El hilo ya enseña el nombre completo. Para cambiar el criterio, solo esta función.
 */
export function nombreEnPrefijo(nombre: string): string {
    return nombre.trim().split(/\s+/)[0] ?? '';
}

export function prefijoDelExtracto(
    c: Pick<Conversacion, 'last_message_origin' | 'last_message_member_name' | 'last_message_member_id'>,
    miNombre: string | null = null,
    miId: string | null = null,
): string {
    switch (c.last_message_origin) {
        case 'staff': {
            const idAutor = c.last_message_member_id ?? null;
            const autor = c.last_message_member_name?.trim() ?? '';
            if (idAutor !== null && miId !== null && idAutor === miId) {
                return 'Tú: ';
            }
            if (autor === '') {
                return '';
            }
            const esMio = idAutor === null && miNombre !== null && autor === miNombre.trim();
            return esMio ? 'Tú: ' : `${nombreEnPrefijo(autor)}: `;
        }
        case 'assistant':
            return 'Asistente: ';
        case 'whatsapp_app':
            return 'Desde la app: ';
        case 'delivery':
            return 'Envío: ';
        case 'automated':
            return 'Automático: ';
        default:
            return '';
    }
}

export function esperaPersona(c: Pick<Conversacion, 'state' | 'assigned_member_id'>): boolean {
    return c.state === 'human' && (c.assigned_member_id ?? null) === null;
}

export function textoEstado(c: Conversacion, q: Quien, nombres: ReadonlyMap<string, string>): { texto: string; aviso: boolean } {
    const [nombreCanal] = canal(c.channel_type);
    if (c.state === 'closed') {
        return { texto: `${nombreCanal} · cerrada`, aviso: false };
    }
    if (c.state === 'assistant') {
        return { texto: `${nombreCanal} · con el asistente`, aviso: false };
    }
    const asignada = c.assigned_member_id ?? null;
    if (asignada === null) {
        return { texto: `${nombreCanal} · espera a una persona`, aviso: true };
    }
    const quien = asignada === q.yo ? 'contigo' : `con ${primerNombre(nombres.get(asignada) ?? 'alguien del equipo')}`;
    return { texto: `${nombreCanal} · ${quien}${c.human_mode_pinned === true ? ' · fijada' : ''}`, aviso: false };
}

// ---- Qué se puede hacer con la conversación abierta ----

export interface Acciones {
    atender: boolean;
    tomar: boolean;
    devolver: boolean;
    asignar: boolean;
    fijar: boolean;
    cerrar: boolean;
    vincular: boolean;
}

export function accionesDe(c: Conversacion, q: Quien, soloLectura: boolean): Acciones {
    const asignada = c.assigned_member_id ?? null;
    const atender = !soloLectura && (q.todas || (asignada !== null && asignada === q.yo));
    const humana = c.state === 'human';
    return {
        atender,
        tomar: !soloLectura && c.state !== 'closed' && (c.state === 'assistant' ? q.todas : asignada === null && (q.todas || q.suyas)),
        devolver: q.asistente && humana && asignada !== null && atender,
        asignar: humana && !soloLectura && (q.todas || asignada === q.yo),
        fijar: q.asistente && humana && asignada !== null && atender,
        // Cerrar se puede también en solo lectura (`x-read-only-mode: allowed`).
        cerrar: c.state !== 'closed' && (q.todas || asignada === q.yo),
        vincular: atender,
    };
}

export type Pie =
    | { tipo: 'caja'; icono: NombreIcono; tono: 'gris' | 'aviso'; titulo: string; texto: string; boton: 'tomar' | 'tomar-sec' | null }
    | { tipo: 'plantilla'; nota: string; texto: string }
    | { tipo: 'escribir'; avisoPrivacidad: boolean; plantillas: boolean; nota: string | null };

export function pieDe(c: Conversacion, q: Quien, soloLectura: boolean, nombres: ReadonlyMap<string, string>, ahora: Date, zona?: string): Pie {
    const nombre = primerNombre(nombreDe(c));
    const asignada = c.assigned_member_id ?? null;
    if (c.state === 'closed') {
        return { tipo: 'caja', icono: 'archive', tono: 'gris', titulo: 'Conversación cerrada.', texto: `Si ${nombre} vuelve a escribir, se abre de nuevo${q.asistente ? ' con el asistente' : ''}.`, boton: null };
    }
    if (soloLectura) {
        return { tipo: 'caja', icono: 'lock', tono: 'gris', titulo: 'Solo lectura.', texto: 'Puedes leer y cerrar conversaciones, pero no responder hasta que se pague la cuota.', boton: null };
    }
    if (c.state === 'assistant') {
        return { tipo: 'caja', icono: 'sparkles', tono: 'gris', titulo: 'Atiende el asistente.', texto: q.todas ? 'Tómala si quieres escribir tú.' : '', boton: q.todas ? 'tomar-sec' : null };
    }
    if (asignada === null) {
        return { tipo: 'caja', icono: 'headset', tono: 'aviso', titulo: 'Espera a una persona.', texto: 'Tómala para responder: quedará asignada a ti.', boton: 'tomar' };
    }
    if (!(q.todas || asignada === q.yo)) {
        return { tipo: 'caja', icono: 'headset', tono: 'gris', titulo: `La atiende ${nombres.get(asignada) ?? 'otra persona del equipo'}.`, texto: 'Solo puede responder quien la tiene asignada.', boton: null };
    }
    const nota = textoVentana(c, ahora, zona);
    if (CON_VENTANA.includes(c.channel_type) && c.window_open === false) {
        return { tipo: 'plantilla', nota: nota ?? '', texto: `${canal(c.channel_type)[0]} solo deja escribir a ${nombre} con una plantilla aprobada por Meta.` };
    }
    return { tipo: 'escribir', avisoPrivacidad: CON_AVISO.includes(c.channel_type) && (c.contact.privacy_notice_sent_at ?? null) === null, plantillas: c.channel_type === 'whatsapp', nota: null };
}

// ---- Fechas ----

const fmt = (opciones: Intl.DateTimeFormatOptions, zona?: string) => new Intl.DateTimeFormat('es-ES', { ...opciones, timeZone: zona });
const diaDe = (f: Date, zona?: string) => fmt({ year: 'numeric', month: '2-digit', day: '2-digit' }, zona).format(f);
const hora = (f: Date, zona?: string) => fmt({ hour: '2-digit', minute: '2-digit', hour12: false }, zona).format(f);

function diasEntre(a: Date, b: Date, zona?: string): number {
    const [da = '', ma = '', ya = ''] = diaDe(a, zona).split('/');
    const [db = '', mb = '', yb = ''] = diaDe(b, zona).split('/');
    return Math.round((Date.UTC(Number(yb), Number(mb) - 1, Number(db)) - Date.UTC(Number(ya), Number(ma) - 1, Number(da))) / 86_400_000);
}

/** Hora de la lista: «13:06» hoy, «Ayer» y después «12 oct». */
export function horaDeLista(iso: string | null | undefined, ahora: Date, zona?: string): string {
    if (iso == null) {
        return '';
    }
    const f = new Date(iso);
    if (Number.isNaN(f.getTime())) {
        return '';
    }
    const d = diasEntre(f, ahora, zona);
    if (d === 0) {
        return hora(f, zona);
    }
    if (d === 1) {
        return 'Ayer';
    }
    return fmt({ day: 'numeric', month: 'short' }, zona).format(f).replace('.', '');
}

export const horaDeMensaje = (iso: string, zona?: string): string => hora(new Date(iso), zona);

/** Separador de días del historial: «Hoy», «Ayer», «Martes 10 de octubre» (con el año si no es este). */
export function separadorDeDia(iso: string, ahora: Date, zona?: string): string {
    const f = new Date(iso);
    const d = diasEntre(f, ahora, zona);
    if (d === 0) {
        return 'Hoy';
    }
    if (d === 1) {
        return 'Ayer';
    }
    const mismoAno = fmt({ year: 'numeric' }, zona).format(f) === fmt({ year: 'numeric' }, zona).format(ahora);
    const texto = fmt(mismoAno ? { weekday: 'long', day: 'numeric', month: 'long' } : { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }, zona).format(f).replace(',', '');
    return texto.charAt(0).toLocaleUpperCase('es') + texto.slice(1);
}

export function claveDeDia(iso: string, zona?: string): string {
    return diaDe(new Date(iso), zona);
}

/** «hoy a las 13:06», «mañana a las 13:06», «ayer a las 19:02», «el lunes a las 18:20» o «el 12 oct a las 18:20». */
function cuandoRelativo(f: Date, ahora: Date, zona?: string): string {
    const d = diasEntre(ahora, f, zona);
    const h = hora(f, zona);
    if (d === 0) {
        return `hoy a las ${h}`;
    }
    if (d === 1) {
        return `mañana a las ${h}`;
    }
    if (d === -1) {
        return `ayer a las ${h}`;
    }
    if (d < 0 && d > -7) {
        return `el ${fmt({ weekday: 'long' }, zona).format(f)} a las ${h}`;
    }
    return `el ${fmt({ day: 'numeric', month: 'short' }, zona).format(f).replace('.', '')} a las ${h}`;
}

/** Aviso de la ventana de 24 h (solo WhatsApp e Instagram). */
export function textoVentana(c: Pick<Conversacion, 'channel_type' | 'window_open' | 'window_expires_at'>, ahora: Date, zona?: string): string | null {
    if (!CON_VENTANA.includes(c.channel_type) || c.window_open == null) {
        return null;
    }
    const fin = c.window_expires_at == null ? null : new Date(c.window_expires_at);
    if (fin === null || Number.isNaN(fin.getTime())) {
        return c.window_open ? 'Ventana de 24 h abierta' : 'Ventana de 24 h cerrada';
    }
    const cuando = cuandoRelativo(fin, ahora, zona);
    return c.window_open ? `Ventana de 24 h abierta hasta ${cuando}` : `Ventana de 24 h cerrada desde ${cuando}`;
}


/** «Enviado el 14 oct a las 12:58, versión 2026-10-21.» o «Aún no se le ha respondido…» (Contact, G191). */
export function textoAvisoPrivacidad(c: Pick<Conversacion, 'contact'>, zona?: string): string {
    const enviado = c.contact.privacy_notice_sent_at ?? null;
    if (enviado === null) {
        return 'Aún no se le ha respondido por este canal: irá delante de la primera respuesta.';
    }
    const f = new Date(enviado);
    const cuando = `${fmt({ day: 'numeric', month: 'short' }, zona).format(f).replace('.', '')} a las ${hora(f, zona)}`;
    const version = c.contact.privacy_notice_version ?? null;
    return `Enviado el ${cuando}${version === null ? '' : `, versión ${version}`}.`;
}

// ---- Mensajes ----

export const ACUSES: Readonly<Record<string, readonly [NombreIcono, string]>> = {
    queued: ['clock', 'En cola'],
    sent: ['check', 'Enviado'],
    delivered: ['check-check', 'Entregado'],
    read: ['check-check', 'Leído'],
};

/** El acuse que se enseña: en Instagram, Telegram y el chat de la web nunca «Leído» (G527). */
export function acuseDe(m: Pick<Mensaje, 'status' | 'direction'>, tipoCanal: string): readonly [NombreIcono, string] | null {
    if (m.direction !== 'outbound' || m.status === 'failed' || m.status === 'received') {
        return null;
    }
    const estado = m.status === 'read' && SIN_LEIDO.includes(tipoCanal) ? 'delivered' : m.status;
    return ACUSES[estado] ?? null;
}

/**
 * Línea gris centrada (cambios de modo y avisos del sistema). Un texto que Canales manda al cliente (`origin = system` con
 * contenido normal y saliente, como «Cerrado por temporada», C.55) no es una línea: es una burbuja de respuesta automática.
 */
export function esLineaDeSistema(m: Pick<Mensaje, 'origin' | 'content_type' | 'direction'>): boolean {
    return m.content_type === 'system' || (m.origin === 'system' && m.direction !== 'outbound');
}

/**
 * Quién firma un texto que Canales mandó solo al cliente (11.3): la respuesta de la pausa de temporada (`system_kind =
 * season_pause`, G1183) firma «Respuesta automática · Cerrado por temporada» (G1055, G1060, C.55); cualquier otra,
 * la confirmación de baja (`system_kind = unsubscribe_confirmation`, G1212, G1218), «Respuesta automática · Baja del chat»
 * (G1216, C.80); cualquier otra, «Respuesta automática». Se reconoce por el propio mensaje, no por el motivo de la
 * conversación.
 */
export function respuestaAutomatica(m: Pick<Mensaje, 'origin' | 'content_type' | 'direction' | 'system_kind'>): { texto: string; icono: NombreIcono } | null {
    if (m.origin !== 'system' || esLineaDeSistema(m)) {
        return null;
    }
    switch (m.system_kind) {
        case 'season_pause':
            return { texto: 'Respuesta automática · Cerrado por temporada', icono: 'calendar-x' };
        case 'unsubscribe_confirmation':
            return { texto: 'Respuesta automática · Baja del chat', icono: 'bell-off' };
        default:
            return { texto: 'Respuesta automática', icono: 'bell' };
    }
}

export function borrado(m: Pick<Mensaje, 'body' | 'media'>): boolean {
    return m.body == null && (m.media ?? []).length === 0;
}

/**
 * Encima de la respuesta que llevó delante el aviso de privacidad (`privacy_notice_version`; T40, T40b o T43; G191, G526,
 * G687, G690, G691): «… y de inteligencia artificial» si respondió el asistente; si respondió una persona o Listas, solo
 * «de privacidad». `body` ya viene sin el texto del aviso.
 */
export function avisoDelMensaje(m: Pick<Mensaje, 'origin' | 'privacy_notice_version'>): string | null {
    if ((m.privacy_notice_version ?? null) === null) {
        return null;
    }
    return m.origin === 'assistant' ? 'Con el aviso de privacidad y de inteligencia artificial' : 'Con el aviso de privacidad';
}

export function tamano(bytes: number | undefined): string {
    if (bytes === undefined) {
        return '';
    }
    if (bytes >= 1_048_576) {
        return `${(bytes / 1_048_576).toLocaleString('es-ES', { maximumFractionDigits: 1 })} MB`;
    }
    return `${String(Math.max(1, Math.round(bytes / 1024)))} KB`;
}

export function etiquetaAdjunto(tipo: string): string {
    return tipo === 'image' ? 'Foto' : tipo === 'audio' ? 'Nota de voz' : tipo === 'video' ? 'Vídeo' : 'Archivo';
}

// ---- Ajustes del buzón ----

export interface BorradorAjustes {
    /** Horas sin actividad para volver al asistente (G446, G689); sin el bloque Asistente IA no se enseña y se guarda la que hay. */
    vuelta: string;
    cierre: string;
    emailOn: boolean;
    espera: string;
    meses: string;
}

export function borradorDe(a: AjustesBuzon): BorradorAjustes {
    const espera = a.escalation_email_after_minutes ?? null;
    return { vuelta: String(a.human_mode_expiry_hours), cierre: String(a.conversation_close_after_hours), emailOn: espera !== null, espera: String(espera ?? 30), meses: String(a.retention_months) };
}

/** «1.440»: el punto de los miles también con cuatro cifras, como en el diseño. */
const miles = (n: number): string => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

function error(v: string, min: number, max: number): string {
    const n = Number(v.trim().replace(',', '.'));
    return v.trim() === '' || !Number.isInteger(n) || n < min || n > max ? `Entre ${String(min)} y ${miles(max)}.` : '';
}

export function erroresAjustes(b: BorradorAjustes): { vuelta: string; cierre: string; espera: string; meses: string } {
    return { vuelta: error(b.vuelta, 1, 168), cierre: error(b.cierre, 1, 168), espera: b.emailOn ? error(b.espera, 5, 1440) : '', meses: error(b.meses, 1, 60) };
}

export function cambioDe(b: BorradorAjustes): CambioAjustesBuzon {
    return {
        // La caducidad del modo humano se cambia aquí (G689); sin el bloque Asistente IA el borrador trae la que hay.
        human_mode_expiry_hours: Number(b.vuelta),
        conversation_close_after_hours: Number(b.cierre),
        retention_months: Number(b.meses),
        escalation_email_after_minutes: b.emailOn ? Number(b.espera) : null,
    };
}

export function sinCambios(b: BorradorAjustes, a: AjustesBuzon): boolean {
    const c = cambioDe(b);
    return c.human_mode_expiry_hours === a.human_mode_expiry_hours && c.conversation_close_after_hours === a.conversation_close_after_hours && c.retention_months === a.retention_months && c.escalation_email_after_minutes === (a.escalation_email_after_minutes ?? null);
}

// ---- Plantillas de Meta ----

export const HUECO = /\{\{\s*([^}]+?)\s*\}\}/g;

export function cuerpoDe(p: PlantillaMeta): string {
    return (p.components ?? []).find((x) => x.type === 'body')?.text ?? '';
}

/** Los huecos de la plantilla, en orden y sin repetir (`{{1}}`, `{{nombre}}`), tal como los nombra Meta. */
export function huecosDe(p: PlantillaMeta): string[] {
    const vistos: string[] = [];
    for (const m of cuerpoDe(p).matchAll(HUECO)) {
        const h = m[1] ?? '';
        if (h !== '' && !vistos.includes(h)) {
            vistos.push(h);
        }
    }
    return vistos;
}
